// Packages and components
import React, { useContext } from 'react';
import { Anchor, Box, Button, Heading, Text } from 'grommet';
import { CircleInformation, LinkPrevious } from 'grommet-icons';
import PaywallList from '@components/Paywall/components/PaywallFeaturesList';
import SettingsCancelSubscription from './SettingsCancelSubscription';
import SettingsConfirmCancelSubscription from './SettingsConfirmCancelSubscription';

// Helpers and utilities
import { useIsFeatureEnabled } from '@helpers/flagUtils';

// Hooks and contexts
import { AppHostContext } from '@services/appHost';
import { useHistory } from 'react-router-dom';
import { useModal } from '@components/ModalContext';
import { UserContext } from '@services/Session';
import { useCancelSubscription } from '@hooks/revcat/useCancelSubscription';

import { usePaywallContext } from '@contexts/PaywallContext';
import { ViewerEntitlementStatus } from '@components/Paywall/paywall.types';

import { usePaywallTracking } from '@hooks/revcat/usePaywallTracking';

// Types and interfaces
import {
  PaywallPageContext,
  PaywallEvents,
} from '@components/Paywall/paywall.types';

import { useUserStats } from '@hooks/useUserStats';

const SettingsManageSubscription: React.FC = () => {
  // Contexts
  const appHost = useContext(AppHostContext);
  const userStore = useContext(UserContext);

  // Hooks
  const { activeSubscription, renderCTA } = usePaywallContext({
    entitlementStatus: ViewerEntitlementStatus.ENTITLED,
  });
  const { showModal, hideModal } = useModal();
  const { trackPaywallEvent } = usePaywallTracking(
    PaywallPageContext.ACCOUNT_SETTINGS
  );
  const history = useHistory();
  const showInternalMonetizationTests = useIsFeatureEnabled(
    'monetization_tests',
    userStore
  );

  const { getStats } = useUserStats();
  const { cancelSubscription } = useCancelSubscription();

  // Constants
  const appHostService = appHost.isHosted ? appHost.service : undefined;
  const isHostedAndroid = async () => {
    const res = await appHostService?.requests.fetchOS();
    return res === 'android';
  };

  const clipCount = async (): Promise<number> => {
    return getStats().then((res) => {
      if (res) {
        return res.clipCount;
      }
      return 0;
    });
  };

  // Handlers
  const handleCancelAccountClick = async () => {
    trackPaywallEvent(PaywallEvents.CANCEL_MEMBERSHIP_CLICKED);
    if (appHost.isHosted) {
      const isDroid = await isHostedAndroid();
      const subscriptionsLink = isDroid
        ? 'https://play.google.com/store/account/subscriptions'
        : 'itms-apps://apps.apple.com/account/subscriptions';
      appHost.service.notifications.openAppLink(subscriptionsLink);
    } else {
      showModal(
        <SettingsCancelSubscription
          numVideos={await clipCount()}
          onClose={() => hideModal()}
          onCancelMembership={async () => {
            await handleConfirmCancelAccountClick();
          }}
        />,
        {
          className: 'is-transparent-controls',
          onClose: () => {
            hideModal();
          },
        }
      );
    }
  };

  const handleConfirmCancelAccountClick = async () => {
    trackPaywallEvent(PaywallEvents.CANCEL_MEMBERSHIP_CLICKED);
    await cancelSubscription().then((resp) => {
      hideModal();
      showModal(
        <SettingsConfirmCancelSubscription
          expirationTs={resp.timeEndTs}
          onClose={() => hideModal()}
        />,
        {
          className: 'is-transparent-controls',
          onClose: () => {
            hideModal();
          },
        }
      );
    });
  };

  const onShowPaywall = () => {
    if (appHost.isHosted) {
      appHost.service.notifications.showPaywall();
    }
  };

  return (
    <Box
      background="#fff"
      pad="large"
      round="0.75rem"
      direction="column"
      height={`100dvh`}
    >
      <Box
        align="center"
        direction="row"
        justify="start"
        margin={{ bottom: '1.3125rem', top: '1rem' }}
      >
        <Anchor
          onClick={() => history.goBack()}
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <LinkPrevious fill="#1a1a1a" size="24px" />
        </Anchor>
        <Heading
          color="#1a1a1a"
          level={1}
          size="1.25rem"
          style={{ margin: '0 0 0 0.5rem' }}
          weight={900}
        >
          Membership Details
        </Heading>
      </Box>

      <Box
        background="#f5f5f5"
        direction="row"
        justify="between"
        align="center"
        round="0.75rem"
        style={{ padding: '1.5rem' }}
      >
        <Box>
          <Text size="0.875rem" color="#212121">
            {activeSubscription.productName}
          </Text>
          {activeSubscription.isPromotional ? (
            <Text size="0.75rem" color="#808080">
              {activeSubscription.productTerm.long}
            </Text>
          ) : (
            <Text size="0.75rem" color="#808080">
              {activeSubscription.productPrice}/
              {activeSubscription.productTerm.short}
            </Text>
          )}
        </Box>
        <Anchor
          icon={<CircleInformation fill="#000" />}
          onClick={() => handleCancelAccountClick()}
        />
      </Box>

      <Heading
        color="#1a1a1a"
        level={2}
        size="1rem"
        style={{ margin: '1rem 0 0 0' }}
        weight={900}
      >
        Your membership includes:
      </Heading>

      <PaywallList iconColor="#9341ff" />

      <Box
        className="settings__cancel-subscription"
        justify="end"
        align="center"
        margin={{ vertical: 'auto' }}
      >
        <Anchor
          color="#1a1a1a"
          size="1rem"
          onClick={() => handleCancelAccountClick()}
        >
          Cancel membership
        </Anchor>
      </Box>
      {showInternalMonetizationTests && (
        <Box gap="medium">
          <Heading level={4}>Internal Testing</Heading>
          <Box>
            <Button primary label="Show Paywall" onClick={onShowPaywall} />
            <Text size="small">
              (should only open the paywall if you are inside the app)
            </Text>
          </Box>
          <Box>
            <Text>Paywall CTA</Text>
            {renderCTA({
              mode: 'fullAccess',
              context: PaywallPageContext.ACCOUNT_SETTINGS,
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SettingsManageSubscription;
